<template>
  <section class="console-panel dash-calendar">
    <div class="console-panel-head">
      <div class="cal-nav">
        <button type="button" class="cal-chevron" aria-label="이전 달" @click="shiftMonth(-1)">
          <v-icon size="18">mdi-chevron-left</v-icon>
        </button>
        <h3 class="console-panel-title cal-label">{{ viewYear }}년 {{ viewMonth }}월</h3>
        <button type="button" class="cal-chevron" aria-label="다음 달" @click="shiftMonth(1)">
          <v-icon size="18">mdi-chevron-right</v-icon>
        </button>
      </div>
      <button type="button" class="console-text-btn" aria-label="공휴일관리로 이동" @click="goHoliday">이동</button>
    </div>
    <div class="cal-split">
      <div class="cal-board">
        <div class="cal-week">
          <span v-for="name in weekNames" :key="name">{{ name }}</span>
        </div>
        <div class="cal-days">
          <span v-for="(cell, index) in calendarCells" :key="index" class="cal-cell">
            <button
              v-if="cell"
              type="button"
              class="cal-day"
              :class="{
                'is-today': isToday(cell),
                'is-picked': isPicked(cell),
                'is-holiday': !!holidayOn(cell),
                'is-sun': index % 7 === 0,
                'is-sat': index % 7 === 6,
              }"
              @click="pickDay(cell)"
            >
              <span class="cal-num">{{ cell }}</span>
              <span v-if="holidayName(cell)" class="cal-name">{{ holidayName(cell) }}</span>
              <i v-if="hasMemo(cell)" class="cal-dot" aria-label="메모 있음" />
            </button>
          </span>
        </div>
      </div>
      <aside class="cal-memo">
        <p class="cal-memo-date">{{ selectedLabel }}</p>
        <p class="cal-memo-hint">{{ selectedHint }}</p>
        <textarea
          v-model="draft"
          maxlength="2000"
          placeholder="이 날에 남겨 둘 말을 적어 주세요"
        />
        <div class="cal-memo-foot">
          <span class="cal-memo-state">{{ memoState }}</span>
          <button type="button" class="cal-memo-save" :disabled="saving" @click="saveMemo">저장</button>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { dashMemoApi, holidayManageApi } from '@/services/api';

interface HolidayItem {
  date: string;
  name: string;
}

const router = useRouter();
const now = new Date();
const weekNames = ['일', '월', '화', '수', '목', '금', '토'];
const weekFull = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

const pad = (value: number) => String(value).padStart(2, '0');

const dateKeyOf = (year: number, month: number, day: number) =>
  `${year}-${pad(month)}-${pad(day)}`;

const viewYear = ref(now.getFullYear());
const viewMonth = ref(now.getMonth() + 1);
const monthHolidays = ref<HolidayItem[]>([]);
const memos = ref<Record<string, string>>({});
const selectedDate = ref(dateKeyOf(now.getFullYear(), now.getMonth() + 1, now.getDate()));
const draft = ref('');
const savedBody = ref('');
const saving = ref(false);
const memoState = ref('');

const calendarCells = computed(() => {
  const firstDow = new Date(viewYear.value, viewMonth.value - 1, 1).getDay();
  const lastDate = new Date(viewYear.value, viewMonth.value, 0).getDate();
  const cells: Array<number | null> = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let day = 1; day <= lastDate; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
});

const holidayMap = computed(() => {
  const map = new Map<string, HolidayItem>();
  monthHolidays.value.forEach((item) => map.set(item.date, item));
  return map;
});

const dateKey = (day: number) => dateKeyOf(viewYear.value, viewMonth.value, day);

const holidayOn = (day: number) => holidayMap.value.get(dateKey(day)) || null;
const holidayName = (day: number) => holidayOn(day)?.name || '';
const hasMemo = (day: number) => !!memos.value[dateKey(day)];

const isToday = (day: number) =>
  viewYear.value === now.getFullYear()
  && viewMonth.value === now.getMonth() + 1
  && day === now.getDate();

const isPicked = (day: number) => dateKey(day) === selectedDate.value;

const selectedParts = computed(() => {
  const [year, month, day] = selectedDate.value.split('-').map((part) => Number(part));
  const date = new Date(year, month - 1, day);
  return { year, month, day, weekday: weekFull[date.getDay()] };
});

const selectedLabel = computed(() => {
  const { month, day, weekday } = selectedParts.value;
  return `${month}월 ${day}일 ${weekday}`;
});

const selectedHint = computed(() => {
  const today = dateKeyOf(now.getFullYear(), now.getMonth() + 1, now.getDate());
  if (selectedDate.value === today) return '오늘';
  return holidayMap.value.get(selectedDate.value)?.name || '';
});

const applyDraft = (date: string) => {
  const body = memos.value[date] || '';
  draft.value = body;
  savedBody.value = body;
  memoState.value = '';
};

const loadHolidays = async () => {
  try {
    const res = await holidayManageApi.listByMonth(viewYear.value, viewMonth.value);
    monthHolidays.value = res.data?.data?.items || [];
  } catch (error) {
    monthHolidays.value = [];
  }
};

const loadMemos = async () => {
  try {
    const res = await dashMemoApi.listMonth(viewYear.value, viewMonth.value);
    const next: Record<string, string> = {};
    (res.data?.data?.items || []).forEach((item: { date: string; body: string }) => {
      if (item?.date && item.body) next[item.date] = item.body;
    });
    memos.value = next;
  } catch (error) {
    memos.value = {};
  }
  if (selectedDate.value.startsWith(`${viewYear.value}-${pad(viewMonth.value)}`)) {
    applyDraft(selectedDate.value);
  }
};

const loadMonth = () => {
  loadHolidays();
  loadMemos();
};

const shiftMonth = async (delta: number) => {
  await saveIfDirty();
  const next = new Date(viewYear.value, viewMonth.value - 1 + delta, 1);
  viewYear.value = next.getFullYear();
  viewMonth.value = next.getMonth() + 1;
  const lastDate = new Date(viewYear.value, viewMonth.value, 0).getDate();
  const day = Math.min(selectedParts.value.day, lastDate);
  const today = dateKeyOf(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const sameMonth = viewYear.value === now.getFullYear() && viewMonth.value === now.getMonth() + 1;
  selectedDate.value = sameMonth ? today : dateKeyOf(viewYear.value, viewMonth.value, day);
  draft.value = '';
  savedBody.value = '';
  memoState.value = '';
  loadMonth();
};

const pickDay = async (day: number) => {
  const next = dateKey(day);
  if (next === selectedDate.value) return;
  await saveIfDirty();
  selectedDate.value = next;
  applyDraft(next);
};

let savingTask: Promise<void> | null = null;

const saveMemo = () => {
  if (savingTask) return savingTask;
  const date = selectedDate.value;
  const body = draft.value.trim();
  if (body === savedBody.value) {
    memoState.value = body ? '저장되어 있습니다' : '';
    return Promise.resolve();
  }
  saving.value = true;
  memoState.value = '저장 중';
  savingTask = (async () => {
    try {
      await dashMemoApi.save(date, body);
      const next = { ...memos.value };
      if (body) next[date] = body;
      else delete next[date];
      memos.value = next;
      if (selectedDate.value === date) {
        draft.value = body;
        savedBody.value = body;
        memoState.value = '저장했습니다';
      }
    } catch (error) {
      if (selectedDate.value === date) memoState.value = '저장하지 못했습니다';
    } finally {
      saving.value = false;
      savingTask = null;
    }
  })();
  return savingTask;
};

const saveIfDirty = async () => {
  if (draft.value.trim() === savedBody.value) return;
  await saveMemo();
};

const goHoliday = () => router.push('/holiday-manage');

onMounted(loadMonth);
</script>
